/*
 * B2B free-units (réassort) resolver (pure logic).
 *
 * Owns the tier maths for the doctor volume offer: buy 8/12/18/24/30 units of one
 * reference, receive +1/+2/+4/+6/+7 of that same reference free.
 *
 * The tier numbers are NOT defined here. They come from the shop metafield
 * custom.universkin_b2b_tiers, the single source of truth shared with the
 * discount function (a separate app), and are passed in as a parsed table.
 * This deliberately avoids the JS/Liquid mirror-drift problem the
 * free-samples substitution chains have -- and, more importantly, keeps this
 * runtime and the function's runtime physically unable to disagree.
 *
 * Loads in the browser as window.B2BFreeUnitsResolver and under Node via require().
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.B2BFreeUnitsResolver = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Parse "8:1,12:2,18:4,24:6,30:7" into a descending table. Descending order is
  // what makes freeUnitsFor a simple first-match-wins scan.
  // Anything unparseable is dropped rather than guessed at: an empty table yields
  // zero free units, which is the safe failure.
  function parseTiers(spec) {
    if (typeof spec !== 'string') return [];
    var tiers = [];
    var parts = spec.split(',');
    for (var i = 0; i < parts.length; i++) {
      var pair = parts[i].trim();
      if (!pair) continue;
      var bits = pair.split(':');
      if (bits.length !== 2) continue;
      var min = parseInt(bits[0], 10);
      var free = parseInt(bits[1], 10);
      if (isNaN(min) || isNaN(free)) continue;
      tiers.push({ min: min, free: free });
    }
    tiers.sort(function (a, b) { return b.min - a.min; });
    return tiers;
  }

  // Free units earned for a paid quantity. Step function capped at the top tier:
  // 50 units earns exactly what 30 does. The table does not repeat.
  function freeUnitsFor(quantity, tiers) {
    if (!tiers || !tiers.length) return 0;
    for (var i = 0; i < tiers.length; i++) {
      if (quantity >= tiers[i].min) return tiers[i].free;
    }
    return 0;
  }

  // Units still needed to reach the next threshold, for the cart nudge.
  // Returns null at or above the top tier — there is nothing left to unlock.
  function nextTier(quantity, tiers) {
    if (!tiers || !tiers.length) return null;
    var ascending = tiers.slice().sort(function (a, b) { return a.min - b.min; });
    for (var i = 0; i < ascending.length; i++) {
      if (quantity < ascending[i].min) {
        return {
          unitsAway: ascending[i].min - quantity,
          free: ascending[i].free
        };
      }
    }
    return null;
  }

  // A free line is marked with the exact string 'true'. The discount function in
  // universkin-app keys off this same property, so the check must stay strict.
  function isFreeUnitLine(item) {
    return !!(item && item.properties && item.properties._isFreeUnit === 'true');
  }

  // Sum paid units per variant. Free lines are skipped, so 8 paid + 1 free stays
  // at tier 8 — that is what makes repeated syncs converge instead of ratchet.
  // One variant can occupy several lines when properties or selling plans differ.
  function groupPaidUnits(items) {
    var byVariant = {};
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (isFreeUnitLine(item)) continue;
      var id = String(item.variant_id);
      byVariant[id] = (byVariant[id] || 0) + item.quantity;
    }
    return byVariant;
  }

  // Compare the cart against the entitlement and return the minimal mutations.
  //   updates: line key -> target quantity (0 removes), for /cart/update.js
  //   adds:    [{ variantId, quantity }], for /cart/add.js
  //
  // `enabled` (the kill switch) gates ADDITIONS ONLY. When false the entitlement
  // is computed as zero, so existing free lines are still removed. Removal is
  // always safe; addition is not.
  function reconcile(options) {
    options = options || {};
    var items = options.items || [];
    var tiers = options.tiers || [];
    var enabled = options.enabled === true;   // fail closed on anything else

    var eligible = {};
    var rawEligible = options.eligibleVariantIds;
    if (rawEligible && typeof rawEligible.forEach === 'function') {
      rawEligible.forEach(function (v) { eligible[String(v)] = true; });
    }

    var paid = groupPaidUnits(items);

    // Entitlement per variant. Stays empty when disabled or nothing qualifies,
    // which sends every existing free line to removal below.
    var entitled = {};
    if (enabled) {
      for (var id in paid) {
        if (!Object.prototype.hasOwnProperty.call(paid, id)) continue;
        if (!eligible[id]) continue;
        var free = freeUnitsFor(paid[id], tiers);
        if (free > 0) entitled[id] = free;
      }
    }

    var updates = {};
    var adds = [];
    var seen = {};

    // Existing free lines: adjust to the entitled quantity, or remove.
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!isFreeUnitLine(item)) continue;
      var vid = String(item.variant_id);
      if (seen[vid]) {
        // A second free line for the same variant is stale (Shopify would have
        // merged identical lines). Keep the first, drop the rest.
        updates[item.key] = 0;
        continue;
      }
      seen[vid] = true;
      var want = entitled[vid] || 0;
      if (want === 0) {
        updates[item.key] = 0;
      } else if (item.quantity !== want) {
        updates[item.key] = want;
      }
    }

    // Entitled variants that have no free line yet.
    for (var vid2 in entitled) {
      if (!Object.prototype.hasOwnProperty.call(entitled, vid2)) continue;
      if (seen[vid2]) continue;
      adds.push({ variantId: Number(vid2), quantity: entitled[vid2] });
    }

    return { updates: updates, adds: adds };
  }

  // --- Audience variant guard ---------------------------------------------
  //
  // Two products (face serum, eye contour serum) sell the same reference to
  // both B2C and B2B via a variant option named "Audience" (values "Doctors"
  // / "Retail"). Eligibility is otherwise checked at product level, so
  // tagging the product would make every variant eligible, including
  // Retail. Locksmith hides Retail variants from doctors, but a doctor who
  // fills their cart while logged out, then logs in, would still earn free
  // units on a Retail line. This guard closes that hole.
  //
  // Given a parsed /products/{handle}.js payload, returns the variant ids
  // allowed to earn free units:
  //   - no "Audience" option at all -> every variant is eligible (the
  //     ordinary case, unchanged from before this guard existed)
  //   - "Audience" present -> only variants whose value is "Doctors"
  //     (case-insensitive); "Retail" and anything else, including a value
  //     that cannot be read, are excluded
  //
  // Fails closed throughout: a malformed payload yields no eligible variants
  // at all, and one variant's unreadable Audience value excludes only that
  // variant, not its siblings.

  // Shopify has returned `options` both as an array of strings
  // (["Audience"]) and an array of objects ([{name: "Audience", ...}])
  // across API versions. Returns:
  //   - the index of "Audience" (case-insensitive), if present
  //   - -1 if `options` is a usable array that simply has no Audience entry
  //     (the ordinary no-Audience-option product)
  //   - null if `options` itself is not a usable array at all -- a real
  //     /products/{handle}.js payload always includes `options` (a plain
  //     product returns ["Title"]), so its absence is malformed input, not
  //     "no Audience option", and must fail closed rather than grant
  //     blanket eligibility.
  function findAudienceOptionIndex(options) {
    if (!Array.isArray(options)) return null;
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      var name = typeof opt === 'string' ? opt : (opt && typeof opt.name === 'string' ? opt.name : null);
      if (typeof name === 'string' && name.toLowerCase() === 'audience') return i;
    }
    return -1;
  }

  // Variants expose the option value two ways: a positional `options` array
  // and flat `option1`/`option2`/`option3` fields. Prefer the array; fall
  // back to the flat field at the same position. Returns null when neither
  // yields a string, which the caller treats as unreadable (fail closed).
  function readVariantOptionValue(variant, index) {
    if (!variant) return null;
    if (Array.isArray(variant.options) && typeof variant.options[index] === 'string') {
      return variant.options[index];
    }
    var flat = variant['option' + (index + 1)];
    return typeof flat === 'string' ? flat : null;
  }

  function eligibleVariantIds(product) {
    if (!product || typeof product !== 'object') return [];
    var variants = product.variants;
    if (!Array.isArray(variants)) return [];

    var audienceIndex = findAudienceOptionIndex(product.options);
    if (audienceIndex === null) return []; // malformed `options`: fail closed entirely

    var ids = [];
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      if (!variant || (typeof variant.id !== 'number' && typeof variant.id !== 'string')) continue;

      if (audienceIndex === -1) {
        // No Audience option on this product: every variant is eligible.
        ids.push(variant.id);
        continue;
      }

      var value = readVariantOptionValue(variant, audienceIndex);
      if (typeof value === 'string' && value.toLowerCase() === 'doctors') {
        ids.push(variant.id);
      }
      // Anything else -- "Retail", another value, or unreadable -- is left out.
    }
    return ids;
  }

  return {
    parseTiers: parseTiers,
    freeUnitsFor: freeUnitsFor,
    nextTier: nextTier,
    isFreeUnitLine: isFreeUnitLine,
    groupPaidUnits: groupPaidUnits,
    reconcile: reconcile,
    eligibleVariantIds: eligibleVariantIds
  };
});
